import { RichText } from "@graphcms/rich-text-react-renderer";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import invariant from "tiny-invariant";

import type { Seo } from "~/generated/schema.server";
import { hygraphSdk } from "~/integrations/hygraph/client";
import { response } from "~/utils/http.server";
import mapSEO from "~/utils/mapSEO.server";

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  try {
    const [{ GetLegalPage }] = await Promise.all([hygraphSdk()]);

    if (!params.slug) {
      invariant(false, "No slug found");
    }

    const pages = await GetLegalPage({ slug: params.slug });

    const page = pages.legalPages[0];

    return {
      ...page,
      ...(page?.seo && {
        seo: mapSEO(page.seo as Seo, page.slug, request.url),
      }),
    };
  } catch (cause) {
    throw response.error(cause, { authSession: null });
  }
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data?.seo) {
    return [{}];
  }

  return [data.seo];
};

const LegalPage = () => {
  const { title, content } = useLoaderData<typeof loader>();

  return (
    <main className="section-padding-x-large pt-[50px] md:pt-[100px]">
      <h1 className="h1 font-bold text-charcoal">{title}</h1>
      <section className="pt-[30px] md:pt-[50px]">
        <RichText
          content={content?.raw}
          renderers={{
            h2: ({ children }) =>
              children ? (
                <h2 className="mb-[20px] mt-[30px] text-[24px] font-semibold text-charcoal">
                  {children}
                </h2>
              ) : (
                <></>
              ),
            h3: ({ children }) =>
              children ? (
                <h3 className="mb-[20px] mt-[30px] text-[20px] font-semibold text-charcoal">
                  {children}
                </h3>
              ) : (
                <></>
              ),
            ul: ({ children }) =>
              children ? (
                <ul className="list-disc pl-[20px]">{children}</ul>
              ) : (
                <></>
              ),
            ol: ({ children }) =>
              children ? (
                <ol className="list-decimal pl-[20px]">{children}</ol>
              ) : (
                <></>
              ),
            li: ({ children }) =>
              children ? <li className="mb-[10px]">{children}</li> : <></>,
            p: ({ children }) =>
              children ? <p className="mb-[10px]">{children}</p> : <></>,
            a: ({ children, href }) => (
              <a
                href={href}
                className="text-purple underline hover:text-purple/80"
              >
                {children}
              </a>
            ),
            bold: ({ children }) =>
              children ? <strong>{children}</strong> : <></>,
          }}
        />
      </section>
    </main>
  );
};

export default LegalPage;
